import { useEffect, useRef, useState, useCallback, createContext, useContext } from 'react';
import API from './api';
import socket from './socket';

const DEVICE_KEY = 'agorax_device_id';

function _uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = _uuid();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export function detectTipoDispositivo() {
  const ua = navigator.userAgent || '';
  const coarse = navigator.maxTouchPoints > 1 && /Android|iPhone|iPad|Mobile/i.test(ua);
  if (/iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
  if (coarse || /Mobi/i.test(ua)) return 'movil';
  return 'pc';
}

export function nombreDispositivo() {
  const ua = navigator.userAgent || '';
  const browsers = [
    ['Edg', 'Edge'], ['OPR', 'Opera'], ['Chrome', 'Chrome'], ['Safari', 'Safari'],
    ['Firefox', 'Firefox'], ['SamsungBrowser', 'Samsung Internet'],
  ];
  let browser = 'Navegador';
  for (const [key, label] of browsers) {
    if (ua.includes(key)) { browser = label; break; }
  }
  const osMatch = ua.match(/Windows NT [\d.]+/) || ua.match(/Android [\d.]+/) ||
    ua.match(/iPhone OS [\d_]+/) || ua.match(/Mac OS X [\d_]+/) || ua.match(/Linux/);
  let os = '';
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  const extra = osMatch && !os ? osMatch[0] : '';
  return [browser, os || extra].filter(Boolean).join(' · ') || 'Dispositivo';
}

export function registrarDispositivoEnSocket() {
  const id = getDeviceId();
  if (!id) return;
  socket.emit('device_register', {
    device_id: id,
    nombre: nombreDispositivo(),
    tipo: detectTipoDispositivo(),
    plataforma: navigator.userAgent || '',
  });
}

const BridgeContext = createContext(null);

export function useDeviceBridge() {
  const ctx = useContext(BridgeContext);
  if (!ctx) throw new Error('useDeviceBridge debe usarse dentro de DeviceBridgeProvider');
  return ctx;
}

export function DeviceBridgeProvider({ children }) {
  const deviceIdRef = useRef(getDeviceId());
  const [dispositivos, setDispositivos] = useState([]);
  const [dispositivosTodos, setDispositivosTodos] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const esperasRef = useRef({});

  const listarDispositivos = useCallback(async () => {
    try {
      const r = await API.get('/dispositivos/mios');
      if (r.data?.status === 'success') {
        const todos = r.data.data || [];
        const self = deviceIdRef.current;
        setDispositivosTodos(todos);
        setDispositivos(todos.filter((d) => d.device_id !== self));
      }
    } catch (e) {
      console.warn('No se pudieron listar dispositivos:', e);
    }
  }, []);

  const renombrarDispositivo = useCallback(async (deviceId, nombre) => {
    await API.post('/dispositivos/renombrar', { device_id: deviceId, nombre });
    await listarDispositivos();
  }, [listarDispositivos]);

  const eliminarDispositivo = useCallback(async (deviceId) => {
    await API.delete(`/dispositivos/${deviceId}`);
    await listarDispositivos();
  }, [listarDispositivos]);

  const cargarPendientes = useCallback(async () => {
    try {
      const r = await API.get('/bridge/pendientes');
      if (r.data?.status === 'success') setPendientes(r.data.data || []);
    } catch (e) {
      console.warn('No se pudieron cargar peticiones pendientes:', e);
    }
  }, []);

  // Registrar dispositivo al conectar + latido periodico para mantener online.
  useEffect(() => {
    const onConnect = () => {
      registrarDispositivoEnSocket();
      listarDispositivos();
      cargarPendientes();
    };
    if (socket.connected) onConnect();
    socket.on('connect', onConnect);

    const heartbeat = setInterval(() => {
      if (socket.connected) {
        socket.emit('device_heartbeat', { device_id: deviceIdRef.current });
      }
    }, 60000);

    socket.on('dispositivos_actualizados', listarDispositivos);
    return () => {
      socket.off('connect', onConnect);
      socket.off('dispositivos_actualizados', listarDispositivos);
      clearInterval(heartbeat);
    };
  }, [listarDispositivos, cargarPendientes]);

  // Peticiones entrantes (soy el destino) -> inbox.
  useEffect(() => {
    const onRequest = (data) => {
      setPendientes((prev) => {
        if (prev.some((p) => p.request_id === data.request_id)) return prev;
        return [
          ...prev,
          {
            request_id: data.request_id,
            tipo: data.tipo,
            params: data.params || {},
            origen_nombre: data.origen_nombre || '',
          },
        ];
      });
    };
    const onCancel = (data) => {
      setPendientes((prev) => prev.filter((p) => p.request_id !== data.request_id));
    };
    socket.on('bridge_request', onRequest);
    socket.on('bridge_cancelado', onCancel);
    return () => {
      socket.off('bridge_request', onRequest);
      socket.off('bridge_cancelado', onCancel);
    };
  }, []);

  // Respuestas a peticiones que yo (origen) inicie -> resolver la promesa en espera.
  useEffect(() => {
    const onResponse = (data) => {
      const resolver = esperasRef.current[data.request_id];
      if (resolver) {
        delete esperasRef.current[data.request_id];
        resolver(data);
      }
    };
    socket.on('bridge_response', onResponse);
    return () => socket.off('bridge_response', onResponse);
  }, []);

  const solicitar = useCallback((destinoDeviceId, tipo, params = {}) => {
    return new Promise((resolve, reject) => {
      API.post('/bridge/solicitud', { destino_device_id: destinoDeviceId, tipo, params })
        .then((r) => {
          const requestId = r.data?.data?.request_id;
          if (!requestId) return reject(new Error('Sin request_id'));
          const timeout = setTimeout(() => {
            delete esperasRef.current[requestId];
            reject(new Error('peticion_expirada'));
          }, 130000);
          esperasRef.current[requestId] = (data) => {
            clearTimeout(timeout);
            if (data.estado === 'completado' || data.estado === 'aceptado') resolve(data);
            else reject(new Error(data.estado || 'peticion_fallida'));
          };
        })
        .catch(reject);
    });
  }, []);

  const responder = useCallback(async (requestId, estado, resultado = null) => {
    await API.post('/bridge/responder', { request_id: requestId, estado, resultado });
    setPendientes((prev) => prev.filter((p) => p.request_id !== requestId));
  }, []);

  const rechazar = useCallback(async (requestId) => {
    await API.post('/bridge/responder', { request_id: requestId, estado: 'rechazado' });
    setPendientes((prev) => prev.filter((p) => p.request_id !== requestId));
  }, []);

  const cancelar = useCallback(async (requestId) => {
    await API.post('/bridge/cancelar', { request_id: requestId });
  }, []);

  const value = {
    deviceId: deviceIdRef.current,
    dispositivos,
    dispositivosTodos,
    listarDispositivos,
    renombrarDispositivo,
    eliminarDispositivo,
    solicitar,
    pendientes,
    cargarPendientes,
    responder,
    rechazar,
    cancelar,
  };

  return <BridgeContext.Provider value={value}>{children}</BridgeContext.Provider>;
}
